/**
 * EmailSuggestModal.jsx — confirm dialog for an inferred email suggestion.
 *
 * Shown when a contact has no email but firm-mates do, and their addresses
 * follow a recognizable convention (see utils/emailPattern.js). Presents the
 * generated address in an editable input with the evidence ("2 of 3 contacts
 * at Ardmore Road follow this pattern, e.g. joey@ardmoreroad.com") so a wrong
 * guess can be fixed or dismissed before anything is saved.
 *
 * Props:
 *   contact    — the row missing an email ({ contact_id, name, _firm_name })
 *   suggestion — { email, pattern, count, total, example: { name, email } }
 *   onSave     — async (email) => void — persists and closes on success
 *   onDismiss  — () => void — wrong guess: hide the chip for this contact
 *   onClose    — () => void
 */

import { useEffect, useState } from 'react';

export default function EmailSuggestModal({ contact, suggestion, onSave, onDismiss, onClose }) {
  const [email, setEmail] = useState(suggestion.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose?.(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const trimmed = email.trim().toLowerCase();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  async function handleSave() {
    if (!valid) { setError('That doesn’t look like an email address.'); return; }
    setBusy(true); setError(null);
    try {
      await onSave(trimmed);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="merge-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="merge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merge-modal__title">Suggested email</div>
        <div style={{ fontSize: 13.5 }}>
          <b>{contact.name}</b>
          <span style={{ color: 'var(--text-3)' }}>{contact._firm_name ? ` · ${contact._firm_name}` : ''}</span>
        </div>
        <div className="merge-modal__row">
          <span className="merge-modal__label">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div className="merge-modal__note">
          Guessed from {suggestion.count} of {suggestion.total} contact{suggestion.total === 1 ? '' : 's'} at
          {' '}{contact._firm_name || 'this firm'} with an email — e.g. {suggestion.example.name} is{' '}
          <b>{suggestion.example.email}</b>. Nothing is saved until you confirm.
        </div>
        {suggestion.conflict && (
          <div className="merge-modal__note" style={{ color: 'var(--text-2)' }}>
            ⚠ The firm's usual pattern gives <b>{suggestion.conflict.email}</b>, but that's
            already {suggestion.conflict.name ? <b>{suggestion.conflict.name}</b> : 'another contact'}'s
            address — so this is a guess at the likely fallback. Double-check it before saving.
          </div>
        )}
        {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
        <div className="merge-modal__footer">
          <button
            className="btn btn--ghost btn--sm"
            onClick={onDismiss}
            disabled={busy}
            title="Hide this suggestion — the guess is wrong and there's nothing to correct it to"
            style={{ marginRight: 'auto' }}
          >
            Wrong guess — dismiss
          </button>
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save email'}
          </button>
        </div>
      </div>
    </div>
  );
}
